import { useEffect, useMemo, useState } from 'react';

import { Close } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  addMonths,
  format,
  isAfter,
  isBefore,
  isEqual,
  parseISO,
  startOfMonth,
} from 'date-fns';
import { useQuery } from 'react-query';
import { getIndicatorDataHistory, getIndicators } from 'src/services/wallet';
import { BaseIndicator, NumberOrNullOrND } from 'src/types/wallet';

import {
  formatIndicatorTooltip,
  formatIndicatorValue,
  formatReferenceDate,
  getIndicatorConfig,
  getMonthStartDateBefore,
  INDICATOR_HISTORY_WINDOW_MONTHS,
  IndicatorHistoryRow,
  prepareHistoricalData,
} from './utils';

type IndicatorDetailsModalProps = {
  document: string;
  targetDate: string;
  indicatorName: string;
  indicatorColumn: string;
  onClose: () => void;
  open: boolean;
  historyWindowMonths?: number;
  hideWindowNavigation?: boolean;
};

export function IndicatorDetailsModal({
  document,
  targetDate,
  indicatorName,
  indicatorColumn,
  onClose,
  open,
  historyWindowMonths = INDICATOR_HISTORY_WINDOW_MONTHS,
  hideWindowNavigation = false,
}: IndicatorDetailsModalProps) {
  const [rawIndicatorData, setRawIndicatorData] = useState<BaseIndicator[]>([]);
  const [displayWindowEndDate, setDisplayWindowEndDate] = useState(targetDate);
  const [historyTargetEndDate, setHistoryTargetEndDate] = useState(targetDate);

  useEffect(() => {
    if (!open) {
      return;
    }

    setDisplayWindowEndDate(targetDate);
    setHistoryTargetEndDate(targetDate);
  }, [open, targetDate, indicatorColumn]);

  const { data: indicators, isLoading: isLoadingIndicators } = useQuery({
    queryKey: ['indicators', indicatorColumn, indicatorName],
    queryFn: () => getIndicators(),
    staleTime: 2 * 60 * 60 * 1000, // 2 hours
  });

  const {
    isLoading: isLoadingIndicatorDataHistory,
    isFetching: isFetchingIndicatorDataHistory,
  } = useQuery({
    queryKey: [
      'indicator-history',
      document,
      historyTargetEndDate,
      historyWindowMonths,
    ],
    queryFn: () =>
      getIndicatorDataHistory(
        document,
        historyTargetEndDate,
        historyWindowMonths,
      ),
    enabled: !!document && !!historyTargetEndDate && !!indicatorColumn,
    keepPreviousData: true,
    onSuccess: (data) => {
      setRawIndicatorData(data);
      setDisplayWindowEndDate(historyTargetEndDate);
    },
  });

  const indicatorDataHistory = useMemo<IndicatorHistoryRow[]>(
    () =>
      prepareHistoricalData(
        rawIndicatorData,
        displayWindowEndDate,
        historyWindowMonths,
      ),
    [displayWindowEndDate, rawIndicatorData, historyWindowMonths],
  );

  const indicatorConfig = useMemo(
    () => getIndicatorConfig(indicatorColumn),
    [indicatorColumn, indicatorName, document],
  );

  const tableHeaders = useMemo(() => {
    const headers = ['Data Referência'];

    if (indicatorConfig.type === 'delta') {
      headers.push(indicatorConfig.relatedLabels?.[0] || 'Valor Base');
      headers.push(indicatorConfig.label);
    } else if (indicatorConfig.type === 'sobre') {
      headers.push(indicatorConfig.relatedLabels?.[0] || 'Numerador');
      headers.push(indicatorConfig.relatedLabels?.[1] || 'Denominador');
      headers.push(indicatorConfig.label);
    } else {
      headers.push(indicatorConfig.label);
    }

    return headers;
  }, [indicatorConfig]);

  const currentIndicator = useMemo(() => {
    return Array.isArray(indicators)
      ? indicators.find((ind) => ind.column === indicatorColumn)
      : undefined;
  }, [indicators, indicatorColumn]);

  const calculationFormula = useMemo(() => {
    return currentIndicator?.mathExpression || 'Não aplicável';
  }, [currentIndicator]);

  const historyWindowStartDate = useMemo(() => {
    if (!displayWindowEndDate) {
      return '';
    }

    return formatReferenceDate(
      getMonthStartDateBefore(displayWindowEndDate, historyWindowMonths - 1),
    );
  }, [displayWindowEndDate, historyWindowMonths]);

  const historyWindowEndDate = useMemo(() => {
    return displayWindowEndDate
      ? formatReferenceDate(displayWindowEndDate)
      : '';
  }, [displayWindowEndDate]);

  const canMoveToNextWindow = useMemo(() => {
    if (!historyTargetEndDate || !targetDate) {
      return false;
    }

    const nextWindowStart = startOfMonth(
      addMonths(parseISO(historyTargetEndDate), historyWindowMonths),
    );
    const maxDate = startOfMonth(parseISO(targetDate));

    return (
      isBefore(nextWindowStart, maxDate) || isEqual(nextWindowStart, maxDate)
    );
  }, [targetDate, historyTargetEndDate, historyWindowMonths]);

  const handlePreviousWindow = () => {
    setHistoryTargetEndDate((currentWindowEndDate) =>
      getMonthStartDateBefore(currentWindowEndDate, historyWindowMonths),
    );
  };

  const handleNextWindow = () => {
    setHistoryTargetEndDate((currentWindowEndDate) => {
      const nextWindowEndDate = startOfMonth(
        addMonths(parseISO(currentWindowEndDate), historyWindowMonths),
      );
      const maxWindowEndDate = startOfMonth(parseISO(targetDate));

      if (isAfter(nextWindowEndDate, maxWindowEndDate)) {
        return format(maxWindowEndDate, 'yyyy-MM-dd');
      }

      return format(nextWindowEndDate, 'yyyy-MM-dd');
    });
  };

  const getValueType = (
    column: keyof BaseIndicator,
  ): 'percentage' | 'amount' => {
    const indicator = Array.isArray(indicators)
      ? indicators.find((ind) => ind.column === column)
      : undefined;
    return indicator?.valueType || 'amount';
  };

  const getValueIndicator = (
    item: BaseIndicator | null,
    showNA: boolean,
    column: keyof BaseIndicator,
  ) => {
    if (showNA || !item) {
      return 'N/A';
    }

    if ((item[column] as NumberOrNullOrND) === 'N/D') {
      return 'N/D';
    }

    return formatIndicatorValue(
      item[column] as NumberOrNullOrND,
      getValueType(column),
    );
  };

  const getTooltipIndicator = (
    item: BaseIndicator | null,
    showNA: boolean,
    column: keyof BaseIndicator,
  ) => {
    if (showNA || !item) {
      return 'Período ainda não contabilizado';
    }

    if ((item[column] as NumberOrNullOrND) === 'N/D') {
      return 'N/D';
    }

    return formatIndicatorTooltip(
      item[column] as NumberOrNullOrND,
      getValueType(column),
    );
  };

  const isLoading = isLoadingIndicators || isLoadingIndicatorDataHistory;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      sx={{
        '& .MuiDialog-paper': {
          width: '100%',
          maxHeight: 'calc(100vh - 64px)',
        },
      }}>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
        }}>
        <Typography variant="h6" align="center">
          {indicatorName || 'Detalhes do Indicador'}
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={(theme) => ({
            position: 'absolute',
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            py: 2,
            flex: 1,
            minHeight: 0,
          }}>
          <Box width="100%">
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: hideWindowNavigation
                  ? 'center'
                  : 'space-between',
                gap: 2,
                mb: 2,
              }}>
              {!hideWindowNavigation && (
                <Button
                  aria-label="Ver seis meses anteriores"
                  disabled={isFetchingIndicatorDataHistory}
                  onClick={handlePreviousWindow}
                  size="small"
                  variant="outlined"
                  startIcon={
                    isFetchingIndicatorDataHistory ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }>
                  6 meses anteriores
                </Button>
              )}
              <Typography
                align="center"
                color="text.secondary"
                variant="body2"
                sx={{ fontWeight: 600 }}>
                {historyWindowStartDate && historyWindowEndDate
                  ? `${historyWindowStartDate} - ${historyWindowEndDate}`
                  : 'Histórico semestral'}
              </Typography>
              {!hideWindowNavigation && (
                <Button
                  aria-label="Ver seis meses posteriores"
                  disabled={
                    !canMoveToNextWindow || isFetchingIndicatorDataHistory
                  }
                  onClick={handleNextWindow}
                  size="small"
                  variant="outlined"
                  endIcon={
                    isFetchingIndicatorDataHistory ? (
                      <CircularProgress color="inherit" size={16} />
                    ) : undefined
                  }>
                  6 meses posteriores
                </Button>
              )}
            </Box>
            <Box
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}>
              {!isLoading && isFetchingIndicatorDataHistory && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'rgba(255, 255, 255, 0.6)',
                    borderRadius: 1,
                  }}>
                  <CircularProgress color="primary" size={32} />
                </Box>
              )}
              {isLoading ? (
                <Skeleton
                  variant="rectangular"
                  animation="wave"
                  sx={{
                    width: '100%',
                    flex: 1,
                    minHeight: 240,
                  }}
                />
              ) : (
                <TableContainer
                  component={Box}
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    maxHeight: 'none',
                  }}>
                  <Table size="medium" stickyHeader>
                    <TableHead>
                      <TableRow>
                        {tableHeaders.map((header, idx) => (
                          <TableCell
                            key={idx}
                            sx={{
                              fontWeight: 'bold',
                              backgroundColor: '#FF821046',
                            }}
                            align="center">
                            {header}
                          </TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {indicatorDataHistory.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={tableHeaders.length}
                            align="center">
                            <Typography variant="body2" color="text.secondary">
                              Nenhum dado disponível para este indicador
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        indicatorDataHistory.map((row, idx) => {
                          const item = row.data;
                          const showNA = row.isEmpty || !item;

                          return (
                            <TableRow
                              key={`${row.document_date}-${idx}`}
                              sx={{
                                backgroundColor: showNA
                                  ? '#fff3cd'
                                  : 'transparent',
                              }}>
                              <TableCell
                                align="center"
                                sx={{
                                  fontWeight: showNA ? 'bold' : 'normal',
                                  color: showNA ? '#856404' : 'inherit',
                                }}>
                                {formatReferenceDate(row.document_date)}
                                {showNA && (
                                  <Typography
                                    variant="caption"
                                    display="block"
                                    sx={{
                                      fontSize: '0.7rem',
                                      fontStyle: 'italic',
                                    }}>
                                    Não contabilizado
                                  </Typography>
                                )}
                              </TableCell>

                              {indicatorConfig.type === 'simple' && (
                                <Tooltip
                                  arrow
                                  title={getTooltipIndicator(
                                    item,
                                    showNA,
                                    indicatorConfig.column,
                                  )}>
                                  <TableCell
                                    align="center"
                                    sx={{ cursor: 'pointer' }}>
                                    {getValueIndicator(
                                      item,
                                      showNA,
                                      indicatorConfig.column,
                                    )}
                                  </TableCell>
                                </Tooltip>
                              )}

                              {indicatorConfig.type === 'delta' &&
                                indicatorConfig.relatedColumns && (
                                  <>
                                    <Tooltip
                                      arrow
                                      title={getTooltipIndicator(
                                        item,
                                        showNA,
                                        indicatorConfig.relatedColumns[0],
                                      )}>
                                      <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer' }}>
                                        {getValueIndicator(
                                          item,
                                          showNA,
                                          indicatorConfig.relatedColumns[0],
                                        )}
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip
                                      arrow
                                      title={getTooltipIndicator(
                                        item,
                                        showNA,
                                        indicatorConfig.column,
                                      )}>
                                      <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer' }}>
                                        {getValueIndicator(
                                          item,
                                          showNA,
                                          indicatorConfig.column,
                                        )}
                                      </TableCell>
                                    </Tooltip>
                                  </>
                                )}

                              {indicatorConfig.type === 'sobre' &&
                                indicatorConfig.relatedColumns && (
                                  <>
                                    <Tooltip
                                      arrow
                                      title={getTooltipIndicator(
                                        item,
                                        showNA,
                                        indicatorConfig.relatedColumns[0],
                                      )}>
                                      <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer' }}>
                                        {getValueIndicator(
                                          item,
                                          showNA,
                                          indicatorConfig.relatedColumns[0],
                                        )}
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip
                                      arrow
                                      title={getTooltipIndicator(
                                        item,
                                        showNA,
                                        indicatorConfig.relatedColumns[1],
                                      )}>
                                      <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer' }}>
                                        {getValueIndicator(
                                          item,
                                          showNA,
                                          indicatorConfig.relatedColumns[1],
                                        )}
                                      </TableCell>
                                    </Tooltip>
                                    <Tooltip
                                      arrow
                                      title={getTooltipIndicator(
                                        item,
                                        showNA,
                                        indicatorConfig.column,
                                      )}>
                                      <TableCell
                                        align="center"
                                        sx={{ cursor: 'pointer' }}>
                                        {getValueIndicator(
                                          item,
                                          showNA,
                                          indicatorConfig.column,
                                        )}
                                      </TableCell>
                                    </Tooltip>
                                  </>
                                )}
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              width: '100%',
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 1,
            }}>
            <Typography variant="h6" align="center" sx={{ fontWeight: 'bold' }}>
              CÁLCULO
            </Typography>
            <Typography
              variant="body1"
              align="center"
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.95rem',
                color: 'text.primary',
                wordBreak: 'break-word',
              }}
              dangerouslySetInnerHTML={{
                __html: calculationFormula,
              }}
            />
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
